import React from "react";
import {
  PrimaryButton,
  Stack,
  TooltipHost,
  TextField
} from "@fluentui/react";
import { Loading } from "../../../services/Loading";
import { getCustomerRHSTicketDetails, deleteCustomerRHSTicket, updateCustomerRHSTicket } from "../../../services/customers/CustomerService";
import { IDataCenter, IRHSTicketDetails, ITicketCountVM } from "../../../vm";
import { ToastContext } from "../../common/ToastProvider";
import { parseJwt } from "../../../services/UtilService";
import moment from "moment";
import { mdiCheckCircleOutline, mdiCloseCircleOutline, mdiPencilOutline, mdiTrashCan } from "@mdi/js";
import { THEME, BRAND_SECONDARY } from "../../../utils/Styles";
import Icon from "@mdi/react";
import { childrenGap, childrenGap8 } from "../../common/Constants";
import { ConfirmDialogContext } from "../../common/ConfirmDialogProvider";
import ManageRhsTicketsDialog from "./ManageRhsTicketsDialog";

export interface RhsTicketsDetailsProps {
  data: ITicketCountVM;
  customerId: string;
  startDate: Date | string;
  endDate: Date | string;
  datacenter: IDataCenter;
}

const TIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
var buildingNameDict: any = {};

const RhsTicketsDetails: React.FC<RhsTicketsDetailsProps> = ({ data, customerId, startDate, endDate, datacenter }) => {
  // custom hooks
  const { showToast } = React.useContext(ToastContext);
  const tokenData = parseJwt();
  const { showConfirmDialog } = React.useContext(ConfirmDialogContext);

  // state
  const [state, setState] = React.useState({
    isLoading: false,
    rhsTickets: [],
    manageAddDialog: {
      isOpen: false,
      id: undefined,
      data: undefined,
    },
    isOnEdit: false,
    updateObj: {
      _id: "",
      activityShortDescription: ""
    }
  } as {
    isLoading: boolean;
    rhsTickets: IRHSTicketDetails[];
    manageAddDialog: {
      isOpen: boolean;
      id?: string;
      data?: IRHSTicketDetails;
    };
    isOnEdit: boolean;
    updateObj: {
      _id: string;
      activityShortDescription: string;
    }
  });

  // hooks
  React.useEffect(() => {
    const asyncFunc = async () => {
      buildingNameDict = datacenter.buildings.reduce((acc: any, d) => {
        acc[d._id] = d.name;
        return acc;
      }, {})
      await getRHSTicketsDetails();
    };
    asyncFunc();
  }, []);

  const getRHSTicketsDetails = async () => {
    setState({ ...state, isLoading: true });
    let result = await getCustomerRHSTicketDetails({ customerId, datacenterId: data.datacenterId, startDate: moment(startDate).format("YYYY-MM-DD"), endDate: moment(endDate).format("YYYY-MM-DD") });
    if (result?.success) {
      const finalData: IRHSTicketDetails[] = [];
      result.data.forEach((d, index) => {
        if (d.rhsPdfDataArray.length > 0) {
          d.rhsPdfDataArray.forEach((ele, dIndex) => {
            let obj: IRHSTicketDetails = {
              _id: d._id,
              srNo: index + 1,
              ticketId: d.ticketId,
              buildingId: d.buildingId,
              activityShortDescription: d.activityShortDescription,
              requestType: d.requestType,
              startDateTime: ele.startDateTime ? moment(ele.startDateTime).format(TIME_FORMAT) : "",
              endDateTime: ele.endDateTime ? moment(ele.endDateTime).format(TIME_FORMAT) : "",
              timeConsumed: ele.timeConsumed as number,
              billableHours: ele.billableHours as number,
              rowSpan: dIndex === 0 ? d.rhsPdfDataArray.length : 0,
              rhsPdfDataArray: [],
              isManuallyAdded: d.isManuallyAdded
            };
            finalData.push(obj);
          })
        } else {
          finalData.push({
            _id: d._id,
            srNo: index + 1,
            ticketId: d.ticketId,
            buildingId: d.buildingId,
            activityShortDescription: d.activityShortDescription,
            requestType: d.requestType,
            startDateTime: "",
            endDateTime: "",
            rhsPdfDataArray: [],
            timeConsumed: 0,
            billableHours: 0,
            rowSpan: 1,
            isManuallyAdded: d.isManuallyAdded
          })
        }
      });
      console.log(finalData)
      setState({
        ...state,
        rhsTickets: finalData,
        isLoading: false,
      });
    } else {
      setState({ ...state, isLoading: false });
      showToast(result?.message || "Error while getting Rhs tickets details", "error");
    }
  };

  const handleDialogOpen = (isOpen: boolean, id?: string) => {
    let rhsTickets: any = undefined;
    if (id && id !== undefined) {
      const selectedItems = state.rhsTickets.filter((d) => d._id === id);
      const pdfArr: any = [];
      selectedItems.forEach((d, index) => {
        if (index === 0) {
          rhsTickets = { ...d };
        }
        pdfArr.push({
          startDateTime: d.startDateTime ? d.startDateTime : "",
          endDateTime: d.endDateTime ? d.endDateTime : "",
          timeConsumed: d.timeConsumed ? d.timeConsumed : undefined,
          billableHours: d.billableHours ? d.billableHours : undefined
        })
      })
      rhsTickets.rhsPdfDataArray = pdfArr;
    }
    setState({
      ...state,
      manageAddDialog: {
        isOpen,
        id,
        data: rhsTickets,
      },
      // isOnEdit: false
    });
  };

  const handleDialogClose = (data: IRHSTicketDetails) => {
    let rhsTickets = [...state.rhsTickets];
    if (data) {
      if (state.manageAddDialog.id !== undefined) {
        let index = 0;
        rhsTickets = rhsTickets.map((d) => {
          if (d._id === state.manageAddDialog.id) {
            d.activityShortDescription = data.activityShortDescription;
            const arrayObj = data.rhsPdfDataArray[index];
            if (data.rhsPdfDataArray && arrayObj) {
              d.startDateTime = arrayObj.startDateTime ? moment(arrayObj.startDateTime).format(TIME_FORMAT) : "";
              d.endDateTime = arrayObj.endDateTime ? moment(arrayObj.endDateTime).format(TIME_FORMAT) : "";
              d.billableHours = arrayObj.billableHours;
              d.timeConsumed = arrayObj.timeConsumed;
            }
            index += 1;
          }
          return d;
        })
        showToast("RHS Ticket updated successfully", "success");
      } else {
        const finalData: any = [];
        let lastIndex = rhsTickets.length > 0 ? rhsTickets[rhsTickets.length - 1].srNo : 0;
        data.rhsPdfDataArray.forEach((d, index) => {
          let obj: IRHSTicketDetails = {
            _id: data._id,
            srNo: (lastIndex as number) + 1,
            ticketId: data.ticketId,
            buildingId: data.buildingId,
            activityShortDescription: data.activityShortDescription,
            requestType: data.requestType,
            startDateTime: d.startDateTime ? moment(d.startDateTime).format(TIME_FORMAT) : "",
            endDateTime: d.endDateTime ? moment(d.endDateTime).format(TIME_FORMAT) : "",
            timeConsumed: d.timeConsumed,
            billableHours: d.billableHours,
            rowSpan: index === 0 ? data.rhsPdfDataArray.length : 0,
            rhsPdfDataArray: [],
            isManuallyAdded: true
          }
          finalData.push(obj);
        })
        rhsTickets = [...rhsTickets, ...finalData];
        showToast("RHS Ticket added successfully", "success");
      }
    }
    setState({
      ...state,
      manageAddDialog: {
        isOpen: false,
        id: undefined,
        data: undefined,
      },
      rhsTickets,
    });
  };

  const deleteRHSTickets = async (item: IRHSTicketDetails) => {
    showConfirmDialog(
      "Are You Sure",
      "Do you want to delete this ticket",
      async () => {
        setState({
          ...state,
          isLoading: true,
        });
        let result = await deleteCustomerRHSTicket(item._id as string);
        if (result?.success) {
          showToast("RHS Ticket deleted successfully", "success");
          let rhsTickets = [...state.rhsTickets];
          rhsTickets = rhsTickets.filter((d) => d._id !== item._id);
          setState({
            ...state,
            rhsTickets,
            isLoading: false,
          });
        } else {
          setState({
            ...state,
            isLoading: false,
          });
          showToast(
            result.message || "Error while deleting rhs tickets",
            "error"
          );
        }
      }
    );
  };

  // const onUpdateDescription = async () => {
  //   if (!state.updateObj.activityShortDescription) {
  //     showToast("Add description before save", "error");
  //   } else {
  //     setState({ ...state, isLoading: true });
  //     const result  = await updateCustomerRHSTicket(state.updateObj);
  //     if (result?.success) {
  //       let rhsTickets = state.rhsTickets.map((d) => {
  //         if (d._id === state.updateObj._id) {
  //           d.activityShortDescription = state.updateObj.activityShortDescription;
  //         }
  //         return d;
  //       })
  //       setState({ ...state, rhsTickets, isLoading: false, isOnEdit: false });
  //     } else {
  //       showToast(result?.message || "Error while updating description", "error");
  //       setState({ ...state, isLoading: false });
  //     }
  //   }
  // }

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Stack tokens={childrenGap}>
        {![2, 4, 5].includes(tokenData.role) && (
          <Stack.Item align="end">
            <PrimaryButton
              text="Add RHS Ticket"
              onClick={() => handleDialogOpen(true)}
              className="borderless-btn bg-white"
            />
          </Stack.Item>
        )}
        <Stack.Item>
          <div className="office-table bg-white">
            <table className="ms-Table bordered-table w-100">
              <thead className="html-table-thead">
                <tr>
                  <th>Sr. No.</th>
                  <th>Building Name</th>
                  <th>RHS Ticket No.</th>
                  <th>Activity Short Description</th>
                  <th>Request Type</th>
                  <th>Start Date & Time</th>
                  <th>End Date & Time</th>
                  <th>Time Consumed (Minutes)</th>
                  <th>Billable Hours</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.rhsTickets.length > 0 ? (
                  state.rhsTickets.map((d, index) => {
                    if (d.rowSpan) {
                      return (
                        <tr key={`${index}`}>
                          <td align="center" rowSpan={d.rowSpan}>{d.srNo + "."}</td>
                          <td align="center" rowSpan={d.rowSpan}>{buildingNameDict[d.buildingId]}</td>
                          <td align="center" rowSpan={d.rowSpan}>{d.ticketId}</td>
                          {(state.isOnEdit && d._id === state.updateObj._id) ? <td>
                            <TextField
                              value={state.updateObj.activityShortDescription}
                              onChange={(event: any) => setState({ ...state, updateObj: { ...state.updateObj, activityShortDescription: event.target.value } })}
                            />
                          </td> : <td rowSpan={d.rowSpan}>{d.activityShortDescription}</td>}
                          <td align="center" rowSpan={d.rowSpan}>{d.requestType}</td>
                          <td>{d.startDateTime}</td>
                          <td>{d.endDateTime}</td>
                          <td>{d.timeConsumed}</td>
                          <td>{d.billableHours}</td>
                          {/* <td rowSpan={d.rowSpan}>{d.timeConsumed}</td>
                                <td rowSpan={d.rowSpan}>{d.billableHours}</td> */}
                          <td align="center" rowSpan={d.rowSpan}>
                            <Stack horizontal tokens={childrenGap8} wrap horizontalAlign="center">
                              {d.isManuallyAdded && (
                                <Stack.Item>
                                  <TooltipHost
                                    content="Delete This Ticket"
                                    calloutProps={{
                                      gapSpace: 0,
                                    }}
                                  >
                                    <span
                                      onClick={() => deleteRHSTickets(d)}
                                    >
                                      <Icon
                                        path={mdiTrashCan}
                                        size={0.8}
                                        className="pointer"
                                        color={
                                          THEME.palette
                                            .themePrimary
                                        }
                                      />
                                    </span>
                                  </TooltipHost></Stack.Item>
                              )}
                              {/* {(d.timeConsumed === 0 || d.billableHours === 0) && (
                                      state.isOnEdit && state.updateObj._id === d._id ? (
                                        <>
                                        <Stack.Item>
                                      <TooltipHost
                                      content="Save"
                                      calloutProps={{
                                        gapSpace: 0,
                                      }}
                                    >
                                      <span
                                        onClick={onUpdateDescription}
                                      >
                                        <Icon
                                          path={mdiCheckCircleOutline}
                                          size={0.8}
                                          className="pointer"
                                          color="green"
                                        />
                                      </span>
                                    </TooltipHost>
                                    </Stack.Item>
                                    <Stack.Item>
                                      <TooltipHost
                                      content="Cancel"
                                      calloutProps={{
                                        gapSpace: 0,
                                      }}
                                    >
                                      <span
                                        onClick={() => setState({ ...state, isOnEdit: false })}
                                      >
                                        <Icon
                                          path={mdiCloseCircleOutline}
                                          size={0.8}
                                          className="pointer"
                                          color="red"
                                        />
                                      </span>
                                    </TooltipHost>
                                    </Stack.Item>
                                      </>
                                      ) : ( */}
                              <Stack.Item>
                                <TooltipHost
                                  content="Edit RHS Tickets"
                                  calloutProps={{
                                    gapSpace: 0,
                                  }}
                                >
                                  <span
                                    // onClick={() => setState({ ...state, isOnEdit: true, updateObj: { _id: d._id as string, activityShortDescription: d.activityShortDescription} })}
                                    onClick={() => handleDialogOpen(true, d._id)}
                                  >
                                    <Icon
                                      path={mdiPencilOutline}
                                      size={0.8}
                                      className="pointer"
                                      color={
                                        THEME.palette
                                          .themePrimary
                                      }
                                    />
                                  </span>
                                </TooltipHost>
                              </Stack.Item>
                              {/* )
                                    )} */}
                            </Stack>
                          </td>
                        </tr>
                      )
                    } else {
                      return (
                        <tr key={`${index}`}>
                          <td>{d.startDateTime}</td>
                          <td>{d.endDateTime}</td>
                          <td>{d.timeConsumed}</td>
                          <td>{d.billableHours}</td>
                        </tr>
                      )
                    }
                  })
                ) : (
                  <tr>
                    <td
                      colSpan={10}
                      align="center"
                    >
                      <br />
                      <i style={{ color: BRAND_SECONDARY }}>
                        No RHS Tickets Found
                      </i>
                      <br />
                      <br />
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Stack.Item>
      </Stack>
      {state.manageAddDialog.isOpen && (
        <ManageRhsTicketsDialog
          handleDialogClose={(data?: any) => handleDialogClose(data)}
          data={state.manageAddDialog.data}
          datacenterId={data.datacenterId}
          customerId={customerId}
          buildingDict={buildingNameDict}
          date={data.date}
        />
      )}
    </React.Fragment>
  );
};

export default RhsTicketsDetails;
